// ======================================================
// MOVIEFRENZY — CLASSEMENTS QUÉBÉCOIS
// ======================================================

const ratingsByYearCache = new Map();


const cleanValue = (value) =>
    String(value ?? "").trim();

const uniqueValues = (values) => [
    ...new Set(
        values
            .map(cleanValue)
            .filter(Boolean)
    ),
];

const normalizeSearchValue = (value) =>
    cleanValue(value)
        // Convertir les ligatures avant de retirer les accents.
        .replace(/[œŒ]/g, "oe")
        .replace(/[æÆ]/g, "ae")
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "")
        .replace(/&/g, " AND ")
        .replace(/[^a-zA-Z0-9]+/g, " ")
        .trim()
        .toLowerCase();

const TITLE_NUMBER_WORDS = {
    two: "2",
    deux: "2",
    three: "3",
    trois: "3",
    four: "4",
    quatre: "4",
    five: "5",
    cinq: "5",
    six: "6",
    seven: "7",
    sept: "7",
    eight: "8",
    huit: "8",
    nine: "9",
    neuf: "9",
    ten: "10",
    dix: "10",
};

const TITLE_ARTICLES = new Set([
    "le",
    "la",
    "les",
    "l",
    "the",
    "a",
    "an",
]);

const normalizeTitleForMatch = (value) => {
    const titleTokens =
        normalizeSearchValue(value)
            .split(" ")
            .filter(Boolean);

    /*
     * TMDB place généralement l’article au début,
     * tandis que le registre peut le placer à la fin.
     */
    while (
        titleTokens.length > 0 &&
        TITLE_ARTICLES.has(titleTokens[0])
    ) {
        titleTokens.shift();
    }

    while (
        titleTokens.length > 0 &&
        TITLE_ARTICLES.has(
            titleTokens[titleTokens.length - 1]
        )
    ) {
        titleTokens.pop();
    }

    return titleTokens
        .map(
            (token) =>
                TITLE_NUMBER_WORDS[token] || token
        )
        .join(" ");
};

const directorKeysMatch = (
    firstDirector,
    secondDirector
) => {
    const firstKey =
        normalizeSearchValue(firstDirector);

    const secondKey =
        normalizeSearchValue(secondDirector);

    if (!firstKey || !secondKey) {
        return false;
    }

    if (firstKey === secondKey) {
        return true;
    }

    const firstTokens = firstKey.split(" ");
    const secondTokens = secondKey.split(" ");

    const shorterTokens =
        firstTokens.length <= secondTokens.length
            ? firstTokens
            : secondTokens;

    const longerTokens =
        firstTokens.length <= secondTokens.length
            ? secondTokens
            : firstTokens;

    /*
     * Permet notamment de rapprocher :
     * Podz
     * Daniel Podz Grou
     */
    if (shorterTokens.length === 1) {
        const singleToken = shorterTokens[0];

        return (
            singleToken.length >= 4 &&
            longerTokens.includes(singleToken)
        );
    }

    return shorterTokens.every((token) =>
        longerTokens.includes(token)
    );
};

const getDirectorName = (director) => {
    if (typeof director === "string") {
        return director;
    }

    return director?.name ?? "";
};

const getReleaseYear = ({
    year,
    releaseDate,
}) => {
    const possibleYear =
        year ??
        String(releaseDate ?? "").slice(0, 4);

    const parsedYear = Number.parseInt(
        possibleYear,
        10
    );

    return Number.isFinite(parsedYear)
        ? parsedYear
        : null;
};

// ======================================================
// CHARGEMENT DU FICHIER JSON CORRESPONDANT À L’ANNÉE
// ======================================================

export const loadQuebecRatingsForYear = async (
    year
) => {
    const parsedYear = Number.parseInt(
        year,
        10
    );

    if (!Number.isFinite(parsedYear)) {
        return [];
    }

    if (ratingsByYearCache.has(parsedYear)) {
        return ratingsByYearCache.get(
            parsedYear
        );
    }

    const publicUrl = (
        process.env.PUBLIC_URL ?? ""
    ).replace(/\/$/, "");

    const fileUrl =
        `${publicUrl}/data/quebec-ratings/${parsedYear}.json`;

    const request = fetch(fileUrl)
        .then(async (response) => {
            /*
             * Une année absente du répertoire ne doit pas
             * empêcher la fiche du film de fonctionner.
             */
            if (response.status === 404) {
                return [];
            }

            if (!response.ok) {
                throw new Error(
                    `Impossible de charger ${fileUrl}`
                );
            }

            /*
             * Certains serveurs React retournent index.html
             * avec un statut 200 lorsqu’un fichier annuel
             * n’existe pas encore, notamment 2027.json.
             */
            const contentType =
                response.headers
                    .get("content-type")
                    ?.toLowerCase() || "";

            if (
                !contentType.includes(
                    "application/json"
                )
            ) {
                return [];
            }

            const records =
                await response.json();

            return Array.isArray(records)
                ? records
                : [];
        })
        .catch((error) => {
            ratingsByYearCache.delete(
                parsedYear
            );

            throw error;
        });

    ratingsByYearCache.set(
        parsedYear,
        request
    );

    return request;
};

// ======================================================
// RECHERCHE D’UN FILM QUÉBÉCOIS
// ======================================================

export const findQuebecRating = async ({
    tmdbId,
    title,
    originalTitle,
    releaseDate,
    year,
    directors = [],
}) => {
    const releaseYear = getReleaseYear({
        year,
        releaseDate,
    });

    if (!releaseYear) {
        return null;
    }

    const searchedTitleKeys = [
        ...new Set(
            uniqueValues([
                title,
                originalTitle,
            ])
                .map(normalizeTitleForMatch)
                .filter(Boolean)
        ),
    ];

    if (searchedTitleKeys.length === 0) {
        return null;
    }

    const searchedDirectorKeys =
        uniqueValues(
            directors.map(getDirectorName)
        ).map(normalizeSearchValue);

    try {
        /*
         * L’année du registre peut différer de celle
         * de TMDB selon la première présentation et
         * la date de sortie publique.
         */
        const candidateYears = [
            releaseYear,
            releaseYear - 1,
            releaseYear + 1,
        ];

        const ratings = (
            await Promise.all(
                candidateYears.map(
                    loadQuebecRatingsForYear
                )
            )
        ).flat();

        const titleMatches = ratings.filter(
            (ratingRecord) => {
                const ratingTitleKeys = [
                    ...new Set(
                        uniqueValues([
                            ...(
                                ratingRecord.titleKeys ||
                                []
                            ),
                            ...(
                                ratingRecord.titles ||
                                []
                            ),
                        ])
                            .map(
                                normalizeTitleForMatch
                            )
                            .filter(Boolean)
                    ),
                ];

                return ratingTitleKeys.some(
                    (titleKey) =>
                        searchedTitleKeys.includes(
                            titleKey
                        )
                );
            }
        );

        if (titleMatches.length === 0) {
            return null;
        }

        if (titleMatches.length === 1) {
            return titleMatches[0];
        }

        /*
         * En cas de titres identiques, utiliser le
         * réalisateur pour départager les résultats.
         */
        if (
            searchedDirectorKeys.length > 0
        ) {
            const directorMatches =
                titleMatches.filter(
                    (ratingRecord) =>
                        ratingRecord
                            .directorKeys
                            ?.some(
                                (directorKey) =>
                                    searchedDirectorKeys
                                        .some(
                                            (
                                                searchedDirectorKey
                                            ) =>
                                                directorKeysMatch(
                                                    directorKey,
                                                    searchedDirectorKey
                                                )
                                        )
                            )
                );

            if (
                directorMatches.length === 1
            ) {
                return directorMatches[0];
            }
        }

        /*
         * Dernier départage : privilégier exactement
         * l’année fournie par TMDB.
         */
        const sameYearMatches =
            titleMatches.filter(
                (ratingRecord) =>
                    ratingRecord.year ===
                    releaseYear
            );

        if (sameYearMatches.length === 1) {
            return sameYearMatches[0];
        }

        return null;
    } catch (error) {
        console.warn(
            "Classement québécois indisponible :",
            error
        );

        return null;
    }
};